import { Button, Modal, Space, message } from 'antd';
import type { FormInstance } from 'antd/es/form/Form';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import dayjs from 'dayjs';
import type { Moment } from 'moment';
import React from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import type CommunityGroups from '@/types/database/communityGroups';
import type { SubscriptionPlan } from '@/types/database/subscriptionPlan';

import type { AddQRCodeFormValues, AuthSettingList } from './types';

const { confirm } = Modal;

const downloadQRCode = async (qrCodeId: string) => {
  return new Promise((resolve, reject) => {
    try {
      resolve(
        (window.location.href = `/ezcon/api/backendEnv/proVersion/downloadQrCode/normalMember?qrCodeId=${qrCodeId}&token=${window.localStorage['feathers-jwt']}`)
      );
    } catch (error) {
      reject(error);
    }
  });
};

const deleteQRCode = async (
  qrCodeId: string,
  getMemberAuthList: () => void
) => {
  try {
    await axios({
      method: 'DELETE',
      url: '/ezcon/api/backendEnv/proVersion/memberQrCodeList',
      headers: {
        authorization: localStorage.getItem('feathers-jwt'),
      },
      data: {
        qrCodeId,
      },
    });
    getMemberAuthList();
    message.success('刪除QR Code成功!');
  } catch (error) {
    message.error('刪除QR Code失敗!');
  }
};

const showDeleteConfirm = (qrCodeId: string, getMemberAuthList: () => void) => {
  confirm({
    title: '確認刪除此QR Code?',
    icon: <ExclamationCircleOutlined />,
    content: '確定請按確定鍵',
    onOk: async () => {
      await deleteQRCode(qrCodeId, getMemberAuthList);
    },
  });
};

export const getColumns = (getMemberAuthList: () => void) => {
  const columns: ColumnsType<AuthSettingList> = [
    {
      title: '社區',
      dataIndex: 'communityName',
    },
    {
      title: '戶號',
      dataIndex: 'householdNumber',
    },
    {
      title: '開通方案',
      dataIndex: 'subscriptionPlans',
      render: (value) => value.join(' / '),
    },
    {
      title: 'QR Code有效截止時間',
      dataIndex: 'qrcodeExpiryTime',
      render: (value) => dayjs(value).format('YYYY/MM/DD'),
    },
    {
      title: '方案生效時間',
      dataIndex: 'planEffectiveTime',
      render: (value) => dayjs(value).format('YYYY/MM/DD'),
    },
    {
      title: '狀態',
      dataIndex: 'isScanned',
      render: (value) => (value ? '已掃' : '未掃'),
    },
    {
      title: '操作',
      dataIndex: 'actions',
      width: 150,
      fixed: 'right',
      render: (_value, record) => {
        return (
          <Space>
            <Button type="primary" onClick={() => downloadQRCode(record._id)}>
              下載QR code
            </Button>
            <Button
              type="primary"
              danger
              onClick={() => showDeleteConfirm(record._id, getMemberAuthList)}
            >
              刪除
            </Button>
          </Space>
        );
      },
    },
  ];

  return columns;
};

export const getCommunityNameOptions = (communityGroups: CommunityGroups[]) => {
  const options = communityGroups.map((communityGroup) => {
    return {
      label: communityGroup.name,
      value: communityGroup.name,
    };
  });

  return options;
};

export const getHouseholdNumberOptions = (
  communityGroups: CommunityGroups[],
  communityName: string
) => {
  let options: { label: string; value: string }[] = [];
  const householdNumbers = communityGroups.find(
    (communityGroup) => communityGroup.name === communityName
  )?.householdNumbers;
  if (householdNumbers) {
    options = householdNumbers.map((householdNumber) => ({
      label: householdNumber,
      value: householdNumber,
    }));
  }

  return options;
};

export const getPlanOptions = (
  subscriptionPlans: SubscriptionPlan[],
  communityName: string
) => {
  const noCommunityPlans = subscriptionPlans.filter(
    (plan) => plan.communityName === null
  );
  let options = noCommunityPlans.map((plan) => ({
    label: plan.planName,
    value: plan.planName,
  }));

  if (communityName) {
    options = subscriptionPlans.reduce(
      (options: { label: string; value: string }[], plan) => {
        if (communityName && plan.communityName === communityName) {
          options.push({
            label: plan.planName,
            value: plan.planName,
          });
        }
        return options;
      },
      []
    );
  }

  return options;
};

export const checkboxValidator = async (form: FormInstance) => {
  const checkboxValues: AddQRCodeFormValues['checkbox'] =
    form.getFieldValue('checkbox');

  if (checkboxValues && (checkboxValues.now || checkboxValues.later)) {
    return await Promise.resolve();
  }

  return await Promise.reject(new Error('請至少勾選任一項'));
};

export const planEffectiveTimeValidator = async (
  value: Moment,
  form: FormInstance
) => {
  const checkboxValues: AddQRCodeFormValues['checkbox'] =
    form.getFieldValue('checkbox');

  if (checkboxValues === undefined) {
    return await Promise.resolve();
  }

  if (checkboxValues.later && !value) {
    return await Promise.reject(new Error('請選擇方案生效時間'));
  }

  if (value && !checkboxValues.later) {
    return await Promise.reject(new Error('請勾選固定時間生效'));
  }

  return await Promise.resolve();
};

export const getPlanId = (
  subscriptionPlans: SubscriptionPlan[],
  planName: string
) => {
  const result = subscriptionPlans.find((plan) => {
    return plan.planName === planName;
  });

  return result?._id;
};
